/** 3D viewer for a connected footing system — edge column at the property
 * line + interior column joined by a connection beam. */

import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport, Grid, Bounds } from '@react-three/drei';
import * as THREE from 'three';

interface Props {
  Lx1: number; Ly1: number; Hf1: number;
  bc1: number; hc1: number; x_col1: number;
  Lx2: number; Ly2: number; Hf2: number;
  bc2: number; hc2: number;
  bb: number; hb: number; L_cols: number;
  df: number; cover: number;
}

export function Connected3DView({
  Lx1, Ly1, Hf1, bc1, hc1, x_col1,
  Lx2, Ly2, Hf2, bc2, hc2,
  bb, hb, L_cols, df, cover,
}: Props) {
  // Frame: property line at x = 0 (left edge of footing 1).
  // Column 1 center → x = x_col1.
  // Column 2 center → x = x_col1 + L_cols.
  // Footing 1 spans 0..Lx1.
  // Footing 2 spans (xCol2 - Lx2/2)..(xCol2 + Lx2/2).
  // y = 0 is grade level (positive up). Footings sit BELOW grade (y in [-df, -df+Hf]).
  // Columns rise from footing top to ~80 cm above grade.
  const xCol1 = x_col1;
  const xCol2 = x_col1 + L_cols;
  const x_f2_min = xCol2 - Lx2 / 2;
  const x_f2_max = xCol2 + Lx2 / 2;
  const totalSpan = x_f2_max;
  const maxY = df + 80;
  const maxZ = Math.max(Ly1, Ly2);

  const maxDim = Math.max(totalSpan, maxZ, maxY);
  const scale = 5 / Math.max(maxDim, 1);

  // Shift world so the system is centered around x = 0 in scene units
  const xMid = totalSpan / 2;

  // Helper: convert cm coordinates → scene units
  // x: 0..totalSpan → centered at xMid
  // y: 0 = grade (positive up); footings have y < 0
  const X = (x_cm: number) => (x_cm - xMid) * scale;
  const Y = (y_cm: number) => y_cm * scale;

  return (
    <Canvas
      camera={{ position: [7, 5, 8], fov: 38, near: 0.05, far: 100 }}
      style={{ width: '100%', height: '100%', background: 'var(--surface-canvas)' }}
      dpr={[1, 2]}
      gl={{ preserveDrawingBuffer: true }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 6, 5]} intensity={0.95} />
      <directionalLight position={[-4, -2, -3]} intensity={0.28} />

      <Grid
        args={[40, 40]} cellSize={0.25} cellThickness={0.4}
        cellColor="#475569" sectionSize={1} sectionThickness={1}
        sectionColor="#334155" fadeDistance={25} fadeStrength={1.2}
        position={[0, Y(0) - 0.001, 0]} infiniteGrid
      />

      <Bounds fit clip observe margin={1.45}>
        {/* SOIL — block between/around footings, below grade */}
        <mesh position={[X(totalSpan / 2), Y(-df / 2), 0]}>
          <boxGeometry args={[totalSpan * scale * 1.4, df * scale, maxZ * scale * 1.8]} />
          <meshStandardMaterial color="#7C2D12" transparent opacity={0.08} roughness={1.0} />
        </mesh>

        {/* PROPERTY LINE — thin red vertical plane at x = 0 */}
        <mesh position={[X(0), Y(-df / 2 + 30), 0]}>
          <boxGeometry args={[0.02, (df + 60) * scale, maxZ * scale * 1.8]} />
          <meshBasicMaterial color="#DC2626" transparent opacity={0.45} />
        </mesh>

        {/* FOOTING 1 (medianera) — under col 1, edge of footing at property line */}
        {(() => {
          const cx = Lx1 / 2;
          const cy = -df + Hf1 / 2;
          return (
            <group>
              <mesh position={[X(cx), Y(cy), 0]}>
                <boxGeometry args={[Lx1 * scale, Hf1 * scale, Ly1 * scale]} />
                <meshStandardMaterial color="#94A3B8" transparent opacity={0.42} roughness={0.85} />
              </mesh>
              <lineSegments position={[X(cx), Y(cy), 0]}>
                <edgesGeometry args={[new THREE.BoxGeometry(Lx1 * scale, Hf1 * scale, Ly1 * scale)]} />
                <lineBasicMaterial color="#94A3B8" />
              </lineSegments>
            </group>
          );
        })()}

        {/* FOOTING 2 (interior) — centered on col 2 */}
        {(() => {
          const cx = (x_f2_min + x_f2_max) / 2;
          const cy = -df + Hf2 / 2;
          return (
            <group>
              <mesh position={[X(cx), Y(cy), 0]}>
                <boxGeometry args={[Lx2 * scale, Hf2 * scale, Ly2 * scale]} />
                <meshStandardMaterial color="#94A3B8" transparent opacity={0.42} roughness={0.85} />
              </mesh>
              <lineSegments position={[X(cx), Y(cy), 0]}>
                <edgesGeometry args={[new THREE.BoxGeometry(Lx2 * scale, Hf2 * scale, Ly2 * scale)]} />
                <lineBasicMaterial color="#94A3B8" />
              </lineSegments>
            </group>
          );
        })()}

        {/* COLUMN 1 — from top of footing 1 to ~80 cm above grade */}
        {(() => {
          const colBot = -df + Hf1;       // top of footing 1
          const colTop = 80;              // 80 cm above grade
          const cy = (colBot + colTop) / 2;
          const colH = colTop - colBot;
          return (
            <group>
              <mesh position={[X(xCol1), Y(cy), 0]}>
                <boxGeometry args={[bc1 * scale, colH * scale, hc1 * scale]} />
                <meshStandardMaterial color="#94A3B8" transparent opacity={0.55} roughness={0.85} />
              </mesh>
              <lineSegments position={[X(xCol1), Y(cy), 0]}>
                <edgesGeometry args={[new THREE.BoxGeometry(bc1 * scale, colH * scale, hc1 * scale)]} />
                <lineBasicMaterial color="#475569" />
              </lineSegments>
            </group>
          );
        })()}

        {/* COLUMN 2 */}
        {(() => {
          const colBot = -df + Hf2;
          const colTop = 80;
          const cy = (colBot + colTop) / 2;
          const colH = colTop - colBot;
          return (
            <group>
              <mesh position={[X(xCol2), Y(cy), 0]}>
                <boxGeometry args={[bc2 * scale, colH * scale, hc2 * scale]} />
                <meshStandardMaterial color="#94A3B8" transparent opacity={0.55} roughness={0.85} />
              </mesh>
              <lineSegments position={[X(xCol2), Y(cy), 0]}>
                <edgesGeometry args={[new THREE.BoxGeometry(bc2 * scale, colH * scale, hc2 * scale)]} />
                <lineBasicMaterial color="#475569" />
              </lineSegments>
            </group>
          );
        })()}

        {/* CONNECTION BEAM — between column faces, top approx at grade level */}
        {(() => {
          const beamX1 = xCol1 + bc1 / 2;
          const beamX2 = xCol2 - bc2 / 2;
          const beamL = beamX2 - beamX1;
          const beamCx = (beamX1 + beamX2) / 2;
          const beamTopY = 0;             // beam top flush with grade
          const beamCy = beamTopY - hb / 2;
          return (
            <group>
              <mesh position={[X(beamCx), Y(beamCy), 0]}>
                <boxGeometry args={[beamL * scale, hb * scale, bb * scale]} />
                <meshStandardMaterial color="#F59E0B" transparent opacity={0.55} roughness={0.7} />
              </mesh>
              <lineSegments position={[X(beamCx), Y(beamCy), 0]}>
                <edgesGeometry args={[new THREE.BoxGeometry(beamL * scale, hb * scale, bb * scale)]} />
                <lineBasicMaterial color="#92400E" />
              </lineSegments>
            </group>
          );
        })()}

        {/* SIMPLIFIED BOTTOM MESH — Footing 1 */}
        {(() => {
          const cx = Lx1 / 2;
          const y_bar = -df + cover + 1;        // ~1 cm above footing bottom
          const bars: React.ReactElement[] = [];
          // X-bars (along x axis), distributed across z
          const n_x = 6;
          for (let i = 0; i < n_x; i++) {
            const usable = Ly1 - 2 * cover;
            const z_cm = -Ly1 / 2 + cover + (i + 0.5) * usable / n_x;
            bars.push(
              <Cylinder key={`f1x-${i}`}
                radius={0.012}
                length={Lx1 * scale * 0.96}
                position={[X(cx), Y(y_bar), z_cm * scale]}
                rotation={[0, 0, Math.PI / 2]}
                color="#F59E0B" />,
            );
          }
          // Y-bars (along z axis), distributed across x
          const n_y = 5;
          for (let i = 0; i < n_y; i++) {
            const usable_x = Lx1 - 2 * cover;
            const x_cm = cover + (i + 0.5) * usable_x / n_y;
            bars.push(
              <Cylinder key={`f1y-${i}`}
                radius={0.012}
                length={Ly1 * scale * 0.96}
                position={[X(x_cm), Y(y_bar + 1.5), 0]}
                rotation={[Math.PI / 2, 0, 0]}
                color="#8B5CF6" />,
            );
          }
          return bars;
        })()}

        {/* SIMPLIFIED BOTTOM MESH — Footing 2 */}
        {(() => {
          const cx = (x_f2_min + x_f2_max) / 2;
          const y_bar = -df + cover + 1;
          const bars: React.ReactElement[] = [];
          const n_x = 6;
          for (let i = 0; i < n_x; i++) {
            const usable = Ly2 - 2 * cover;
            const z_cm = -Ly2 / 2 + cover + (i + 0.5) * usable / n_x;
            bars.push(
              <Cylinder key={`f2x-${i}`}
                radius={0.012}
                length={Lx2 * scale * 0.96}
                position={[X(cx), Y(y_bar), z_cm * scale]}
                rotation={[0, 0, Math.PI / 2]}
                color="#F59E0B" />,
            );
          }
          const n_y = 6;
          for (let i = 0; i < n_y; i++) {
            const usable_x = Lx2 - 2 * cover;
            const x_cm = x_f2_min + cover + (i + 0.5) * usable_x / n_y;
            bars.push(
              <Cylinder key={`f2y-${i}`}
                radius={0.012}
                length={Ly2 * scale * 0.96}
                position={[X(x_cm), Y(y_bar + 1.5), 0]}
                rotation={[Math.PI / 2, 0, 0]}
                color="#8B5CF6" />,
            );
          }
          return bars;
        })()}
      </Bounds>

      <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={['#22C55E', '#F59E0B', '#3B82F6']} labelColor="white" />
      </GizmoHelper>
    </Canvas>
  );
}

function Cylinder({ radius, length, position, rotation, color }: {
  radius: number; length: number;
  position: [number, number, number]; rotation: [number, number, number];
  color: string;
}) {
  const geom = useMemo(() => new THREE.CylinderGeometry(radius, radius, length, 14), [radius, length]);
  return (
    <mesh position={position} rotation={rotation} geometry={geom}>
      <meshStandardMaterial color={color} metalness={0.45} roughness={0.4} />
    </mesh>
  );
}
